import {
  createStyleMixin,
  draggableMixin,
  componentNameValidationMixin,
  createDynamicDataMixin,
} from '@descope-ui/common/components-mixins';
import { compose } from '@descope-ui/common/utils';
import {
  forwardAttrs,
  getComponentName,
  injectStyle,
} from '@descope-ui/common/components-helpers';
import { createBaseClass } from '@descope-ui/common/base-classes';
import { TextClass } from '@descope-ui/descope-text/class';

export const componentName = getComponentName('recovery-codes');

const itemRenderer = ({ value }, _, ref) => {
  return `
    <descope-text variant="${ref.variant}" mode="${ref.mode}">
      <span>${value}</span>
    </descope-text>
  `;
};

class RawRecoveryCodes extends createBaseClass({
  componentName,
  baseSelector: ':host > div',
}) {
  static get observedAttributes() {
    return ['readonly'];
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
      <div class="wrapper">
        <div class="list"></div>
        <descope-text class="icon-wrapper">
          <vaadin-icon class="icon" icon="vaadin:copy-o" title="Copy"></vaadin-icon>
        </descope-text>
      </div>
    `;

    this.list = this.shadowRoot.querySelector('.list');
    this.icon = this.shadowRoot.querySelector('.icon');
    this.iconWrapper = this.shadowRoot.querySelector('.icon-wrapper');

    injectStyle(
      `
      :host {
        display: inline-block;
        position: relative;
      }
      .wrapper {
        display: flex;
        justify-content: space-between;
      }
      .list {
        display: flex;
        flex-grow: 1;
        flex-direction: column;
      }
      descope-text span {
        display: inline-flex;
        align-items: center;
        width: fit-content;
      }
      descope-text span::before {
        content: '\\02022';
        transform: translateY(-0.06em);
      }
      .icon-wrapper {
        align-self: flex-start;
      }
      .icon {
        cursor: pointer;
      }
      .icon.copied {
        cursor: initial;
      }
      .icon.readonly {
        pointer-events: none;
      }
    `,
      this,
    );
  }

  get variant() {
    return this.getAttribute('variant');
  }

  get mode() {
    return this.getAttribute('mode');
  }

  init() {
    super.init();

    this.icon.addEventListener('click', this.onCopy.bind(this));

    forwardAttrs(this, this.iconWrapper, { includeAttrs: ['variant', 'mode'] });
  }

  onCopy() {
    navigator.clipboard.writeText(
      String(this.data.map((rc) => rc.value).join('\n')),
    );

    this.icon.setAttribute('icon', 'vaadin:check-circle-o');
    this.icon.setAttribute('title', 'Copied');
    this.icon.classList.add('copied');

    setTimeout(() => {
      this.icon.setAttribute('icon', 'vaadin:copy-o');
      this.icon.setAttribute('title', 'Copy');
      this.icon.classList.remove('copied');
    }, 5000);
  }

  onReadOnly(isReadOnly) {
    this.icon.classList.toggle('readonly', isReadOnly);
  }

  attributeChangedCallback(attrName, oldValue, newValue) {
    super.attributeChangedCallback?.(attrName, oldValue, newValue);

    if (newValue !== oldValue) {
      if (attrName === 'readonly') {
        this.onReadOnly(newValue === 'true');
      }
    }
  }
}

export const RecoveryCodesClass = compose(
  createStyleMixin({
    mappings: {
      hostWidth: { selector: () => ':host', property: 'width' },
      hostMinWidth: { selector: () => ':host', property: 'min-width' },
      hostDirection: [
        { selector: () => ':host', property: 'direction' },
        {
          selector: () => TextClass.componentName,
          property: TextClass.cssVarList.hostDirection,
        },
      ],
      textAlign: { selector: () => '.list', property: 'align-items' },
      iconColor: { selector: () => 'vaadin-icon', property: 'color' },
      iconSize: { selector: () => 'vaadin-icon', property: 'font-size' },
      iconGap: { selector: () => '.wrapper', property: 'gap' },
      bulletGap: { selector: () => 'descope-text span', property: 'gap' },
    },
  }),
  createDynamicDataMixin({
    itemRenderer,
    rerenderAttrsList: ['variant', 'mode'],
    targetSelector: '.list',
  }),
  draggableMixin,
  componentNameValidationMixin,
)(RawRecoveryCodes);
